import {Text} from '@primer/react-brand'
import {useCallback, useEffect, useRef, useState, type KeyboardEvent} from 'react'
import SvgBorder, {type Gradient} from '../SvgBorder/SvgBorder'

interface ToggleItem {
  label: string
  value: string
}

interface ToggleProps {
  items: ToggleItem[]
  onToggle: (value: string) => void
  defaultIndex?: number
  ariaLabel?: string
}

const defaultProps: Partial<ToggleProps> = {
  defaultIndex: 0,
  ariaLabel: '',
}

const PADDING = 8 // px
const BORDER_RADIUS = 20 // px
const BORDER_GRADIENT: Gradient[] = [
  {color: '#fff', offset: 0},
  {color: '#bbb', offset: 100},
]

const Toggle: React.FC<ToggleProps> = props => {
  const initializedProps = {...defaultProps, ...props}
  const {items, onToggle, defaultIndex, ariaLabel} = initializedProps

  const [currentIndex, setCurrentIndex] = useState<number>(defaultIndex as number)
  const currentIndexRef = useRef<number>(currentIndex)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const selectionRef = useRef<SVGSVGElement>(null)

  const onClick = useCallback((itemIndex: number) => {
    setCurrentIndex(itemIndex)
  }, [])

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!wrapperRef.current) return

      const tabs = Array.from(wrapperRef.current.querySelectorAll('[role="tab"]'))

      if (document.activeElement instanceof HTMLElement) {
        const currentTabIndex = tabs.indexOf(document.activeElement)
        let newTabIndex = currentTabIndex

        // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        if (event.key === 'ArrowLeft') {
          // Move to the previous tab, wrapping around if necessary
          newTabIndex = currentTabIndex === 0 ? tabs.length - 1 : currentTabIndex - 1
          // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
        } else if (event.key === 'ArrowRight') {
          // Move to the next tab, wrapping around if necessary
          newTabIndex = currentTabIndex === tabs.length - 1 ? 0 : currentTabIndex + 1
        }

        if (newTabIndex !== currentTabIndex && tabs[newTabIndex]) {
          // @ts-expect-error - TS doesn't know that it's always defined
          tabs[newTabIndex].focus()
          onClick(newTabIndex)
        }
      }
    },
    [onClick],
  )

  const updateSelection = useCallback(() => {
    if (!wrapperRef.current || !selectionRef.current) return

    const tabs: NodeListOf<HTMLButtonElement> = wrapperRef.current.querySelectorAll('[role="tab"]')
    const selectedTab = tabs[currentIndexRef.current]
    if (!selectedTab) return

    // Force a reflow to ensure responsiveness
    selectionRef.current.style.display = 'none'
    selectionRef.current.clientHeight
    selectionRef.current.style.display = ''

    selectionRef.current.style.width = `${selectedTab.offsetWidth}px`
    selectionRef.current.style.height = `${selectedTab.offsetHeight}px`
    selectionRef.current.style.transform = `translate(${selectedTab.offsetLeft - PADDING}px, ${
      selectedTab.offsetTop - PADDING
    }px)`
  }, [])

  useEffect(() => {
    currentIndexRef.current = currentIndex

    if (items[currentIndex]) {
      updateSelection()
      onToggle(items[currentIndex].value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex])

  useEffect(() => {
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', updateSelection)

    return () => {
      window.removeEventListener('resize', updateSelection)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div ref={wrapperRef} className="ToggleContainer" role="tablist" aria-label={ariaLabel}>
      <div className="Toggle">
        <SvgBorder
          ref={selectionRef}
          gradient={BORDER_GRADIENT}
          borderRadius={BORDER_RADIUS}
          className="ToggleSelection"
        />

        {items.map((item, index) => (
          <button
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`item_${index}`}
            role="tab"
            className={`ToggleButton ${currentIndex === index ? 'ToggleButton--selected' : ''}`}
            onClick={() => onClick(index)}
            onKeyDown={onKeyDown}
          >
            <Text weight="medium">{item.label}</Text>
          </button>
        ))}
      </div>
    </div>
  )
}

export default Toggle
